<script setup lang="ts">
import { WdsHeading, WdsStack, WdsIcon, WdsText, WdsButtonGhost } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ComputedRef, ref } from 'vue'
import { $t } from '@/composables/translation'
import { graphql, useFragment } from '@/gql'
import useDate from '@/composables/useDate'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'

const props = defineProps<{
  hireId: string
}>()

const HirePageDocumentsFragment = graphql(`
  fragment HirePageDocumentsType on Hire {
    id
    conversation {
      id
      url
      viewerCanAccess
    }
    latestContract {
      id
      files {
        id
        name
        url
        createdAt
      }
      contractDocuments {
        id
        docType
        pdfUrl
        createdAt
      }
    }
    classification {
      id
      title
      pdfUrl
      createdAt
    }
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query HirePageDocumentsData($id: ID!) {
      hire(id: $id) {
        ...HirePageDocumentsType
      }
    }
  `),
  () => ({
    id: props.hireId,
  }),
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => useFragment(HirePageDocumentsFragment, result.value?.hire))

const orderDesc = ref(true)

interface DocumentFile {
  id: number | string
  name: string
  date: string
  url: string
}

function sortFiles(files: DocumentFile[]): DocumentFile[] {
  return [...files].sort(({ date: d1 }, { date: d2 }) =>
    orderDesc.value
      ? new Date(d2).getTime() - new Date(d1).getTime()
      : new Date(d1).getTime() - new Date(d2).getTime(),
  )
}

function formatFiles(files: DocumentFile[]): DocumentFile[] {
  return sortFiles(files).map(({ id, name, url, date }) => {
    const { date: formatted } = useDate(date, 'YYYY-MM-DD')
    return { id, name, url, date: formatted.value }
  })
}

const contractDocuments: ComputedRef<DocumentFile[]> = computed(() => {
  const contractDocs = hire.value?.latestContract?.contractDocuments ?? []
  return formatFiles(
    contractDocs.map((doc) => ({
      id: doc.id,
      name: doc.docType ?? 'Unknown',
      date: doc.createdAt,
      url: doc.pdfUrl ?? '',
    })),
  )
})

const complianceDocuments: ComputedRef<DocumentFile[]> = computed(() => {
  const classification = hire.value?.classification
  if (!classification) return []
  return formatFiles([
    {
      id: classification.id,
      name: classification.title,
      date: classification.createdAt,
      url: classification.pdfUrl,
    },
  ])
})

const conversationDocuments: ComputedRef<DocumentFile[]> = computed(() => {
  const files = hire.value?.latestContract?.files ?? []
  return formatFiles(
    files.map((file) => ({
      id: file.id,
      name: file.name,
      date: file.createdAt,
      url: file.url,
    })),
  )
})

const conversationUrl = computed(() =>
  hire.value?.conversation?.viewerCanAccess ? (hire.value.conversation.url ?? null) : null,
)

interface DocumentSection {
  title: string
  files: DocumentFile[]
  actionUrl?: string | null
}

const sections = computed<DocumentSection[]>(() =>
  [
    { title: $t('Contract'), files: contractDocuments.value },
    { title: $t('Compliance & Classification'), files: complianceDocuments.value },
    {
      title: $t('Shared in Conversation'),
      files: conversationDocuments.value,
      actionUrl: conversationUrl.value,
    },
  ].filter((section) => section.files.length > 0),
)

const noDocuments = computed(() => sections.value.every(({ files }) => files.length === 0))
</script>

<template>
  <WdsStack
    direction-column
    class="tw-w-full tw-p-3 tw-rounded tw-border tw-border-gray-300 tw-bg-true-gray-50"
  >
    <WdsStack justify-space-between align-center>
      <WdsHeading class="tw-text-lg" type="title3">{{ $t('Documents') }}</WdsHeading>
      <WdsButtonGhost class="tw-text-primary tw-font-medium" @click="orderDesc = !orderDesc">
        <WdsStack justify-center align-center gap-sm>
          <WdsText type="title3" class="tw-font-medium tw-text-gray-600">
            {{ $t('Date added') }}
          </WdsText>
          <WdsStack
            justify-center
            align-center
            gap-xs
            class="tw-transition-all"
            :class="{
              'tw-rotate-0': orderDesc,
              'tw-rotate-180': !orderDesc,
            }"
          >
            <WdsIcon name="chevron-down" :size="16" primary />
          </WdsStack>
        </WdsStack>
      </WdsButtonGhost>
    </WdsStack>

    <WdsStack v-if="loading">
      <LoadingList />
    </WdsStack>
    <WdsStack v-else-if="noDocuments" direction-column gap-sm class="tw-mt-4">
      <WdsStack justify-space-between align-center>
        <WdsText secondary>
          <span class="tw-text-sm tw-font-semibold tw-uppercase">
            {{ $t('No Documents Found') }}
          </span>
        </WdsText>
      </WdsStack>
    </WdsStack>
    <template v-else>
      <WdsStack
        v-for="section in sections"
        :key="section.title"
        direction-column
        gap-sm
        class="tw-mt-4"
      >
        <WdsStack justify-space-between align-center>
          <WdsText secondary>
            <span class="tw-text-sm tw-font-semibold tw-uppercase">
              {{ section.title }}
            </span>
          </WdsText>
          <WdsButtonGhost v-if="section.actionUrl" secondary :href="section.actionUrl">
            <WdsStack align-center gap-sm>
              <span>{{ $t('Add') }}</span>
              <WdsIcon name="chevron-right" :size="16" />
            </WdsStack>
          </WdsButtonGhost>
        </WdsStack>
        <WdsStack
          v-for="document in section.files"
          :key="document.id"
          justify-space-between
          align-center
          class="tw-bg-white tw-p-2 tw-rounded"
        >
          <WdsStack direction-column>
            <WdsText class="tw-font-medium" :title="document.name">
              <div class="tw-truncate tw-max-w-72">
                {{ document.name }}
              </div>
            </WdsText>
            <WdsText class="tw-text-sm" secondary>{{ document.date }}</WdsText>
          </WdsStack>
          <WdsButtonGhost sm :href="document.url" target="_blank">
            <WdsIcon name="download" :size="16" color="#9999AA" />
          </WdsButtonGhost>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsStack>
</template>

<style lang="scss" scoped></style>
